/**
 * ActiveHandoffLinks — "Active hand-off links" widget on the student
 * record. Lists every not-yet-revoked, not-yet-expired hand-off link
 * the SLP has minted for this student, with a one-tap "Revoke" button
 * per row. Mirrors the DocuSign "Active envelopes" UX.
 *
 * Reads via the SECURITY DEFINER RPC `get_active_handoff_tokens_for_session`,
 * bypasses the parent_handoff_tokens deny-by-default RLS, and returns
 * only rows for (session_id, owner_id=auth.uid()) where the token is
 * still alive.
 *
 * The RPC's first-view anchor logic reuses the schema's expires_at
 * semantics — unviewed tokens have expires_at IS NULL and are shown
 * as "Pending — link becomes active when the parent opens it."
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';

interface ActiveToken {
  id: string;
  token: string;
  session_id: string;
  ttl_seconds: number;
  expires_at: string | null;
  first_view_at: string | null;
  view_count: number;
  last_view_at: string | null;
  revoked_at: string | null;
  consumed_at: string | null;
}

interface Props {
  studentId: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

export function ActiveHandoffLinks({ studentId }: Props): ReactElement | null {
  const [tokens, setTokens] = useState<ActiveToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: rpcErr } = await supabase.rpc(
        'get_active_handoff_tokens_for_student',
        { p_student_id: studentId },
      );
      if (cancelled) return;
      if (rpcErr) {
        setError(rpcErr.message);
      } else {
        setTokens((data ?? []) as ActiveToken[]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  async function handleRevoke(tokenId: string): Promise<void> {
    if (revokingId) return;
    if (
      !window.confirm(
        'Revoke this hand-off link? The parent will see "Link revoked" if they open it again.',
      )
    ) {
      return;
    }
    setRevokingId(tokenId);
    const { error: invokeErr } = await supabase.functions.invoke(
      'api-revoke-parent-handoff-token',
      { body: { token_id: tokenId, reason: 'SLP revoked from student record' } },
    );
    if (invokeErr) {
      setError(invokeErr.message);
    } else {
      setTokens((prev) => prev.filter((t) => t.id !== tokenId));
    }
    setRevokingId(null);
  }

  if (loading) {
    return <p className="tagline">Loading active links…</p>;
  }
  if (error) {
    return <p className="error">{error}</p>;
  }
  if (tokens.length === 0) {
    return null;
  }

  return (
    <section className="active-handoff-links">
      <h3>Active hand-off links</h3>
      <p className="tagline">
        Links expire 14 days after first view (FERPA-aligned).
        Revoke immediately if a link went to the wrong recipient.
      </p>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Link suffix</th>
            <th>First view</th>
            <th>Expires</th>
            <th>Views</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tokens.map((t) => (
            <tr key={t.id}>
              <td>
                <code>{t.token.slice(0, 8)}…</code>
              </td>
              <td>
                {t.first_view_at
                  ? formatDate(t.first_view_at)
                  : 'Pending — link becomes active on first view'}
              </td>
              <td>{formatDate(t.expires_at)}</td>
              <td>{t.view_count}</td>
              <td>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => void handleRevoke(t.id)}
                  disabled={revokingId === t.id}
                >
                  {revokingId === t.id ? 'Revoking…' : 'Revoke'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
